
export enum SectionType {
  HERO = 'HERO',
  TEXT_IMAGE = 'TEXT_IMAGE',
  FEATURES = 'FEATURES',
  CTA = 'CTA',
  DOWNLOADS = 'DOWNLOADS',
  PORTFOLIO = 'PORTFOLIO',
  MAP = 'MAP',
  PRODUCT_GRID = 'PRODUCT_GRID',
  RICH_TEXT = 'RICH_TEXT' // New section for legal docs
}

export type RotationMode = 'timer' | 'daily' | 'weekly';

export interface DiscountConfig {
  isActive: boolean;
  type: 'PERCENT' | 'FIXED';
  value: number;
  startDate?: string;
  endDate?: string;
  quantityLimit?: number;
}

export interface ProductVariant {
  id: string;
  name: string; // e.g. "Pojemność 25L"
  price: string; // Override price
  purchasePrice?: string; // Cost
  available: boolean;
  attributes?: Record<string, string>; // New: Variant specific attributes for deep filtering
}

export type ShippingSize = 'parcel_locker' | 'courier_only';

// New: Source configuration for dynamic grids
export type ProductSourceType = 'manual' | 'dynamic_tag' | 'dynamic_random';

export interface Section {
  id: string;
  type: SectionType;
  title?: string;
  subtitle?: string;
  content?: string;
  imageUrl?: string;
  imageAlt?: string;
  
  // Filtering Configuration
  filterCategories?: {
      key: string; // internal id e.g. "capacity"
      label: string; // display label e.g. "Pojemność Złoża"
      options?: string[]; // Predefined values e.g. ["25L", "30L"]
  }[];

  // Dynamic Product Source Config
  productSource?: ProductSourceType;
  sourceCriteria?: string; // e.g. tag name "Bestseller"
  itemLimit?: number; // How many items to show in dynamic mode

  items?: { 
    id?: string; // Unique Product ID
    title: string; 
    description: string; 
    longDescription?: string; // HTML/Markdown allowed
    icon?: string; 
    link?: string;
    category?: string;
    imageUrl?: string; 
    gallery?: string[];
    // E-commerce fields
    price?: string;
    oldPrice?: string;
    purchasePrice?: string; // Cost of goods sold
    basePrice?: string; // Original price before discount
    shippingSize?: ShippingSize; // New field for dimensions logic
    badge?: string; // e.g. "NOWOŚĆ", "PROMOCJA"
    variants?: ProductVariant[];
    relatedProductIds?: string[]; // Recommended products
    discountConfig?: DiscountConfig;
    // Attributes for filtering
    attributes?: Record<string, string>; // { "capacity": "25L", "color": "Black" }
    // User feedback
    rating?: number;
    reviewsCount?: number;
  }[];
  buttonText?: string;
  buttonLink?: string;
  
  // Rotation / Gallery props
  galleryImages?: string[];
  isAutoRotationEnabled?: boolean;
  rotationMode?: RotationMode;
  rotationInterval?: number; // seconds for timer mode
}

export interface Page {
  id: string;
  slug: string;
  title: string;
  metaDescription: string;
  sections: Section[];
}

export interface SiteConfig {
  primaryColor: string;
  secondaryColor: string;
  companyName: string;
  companyTagline?: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  logoUrl?: string;
  faviconUrl?: string;
  facebookUrl?: string;
  googleMapsUrl?: string;
  showCelebrationBanner: boolean;
  googleAnalyticsId?: string;
  // PayU Configuration
  payuPosId?: string;
  payuClientSecret?: string;
  payuMd5Key?: string;
  payuEnv?: 'sandbox' | 'secure';
  // Integrations
  geminiApiKey?: string;
  baseLinkerToken?: string;
  inPostGeowidgetToken?: string;
  // Admin Security
  adminToken?: string; // Simple token for accessing orders.json
  
  // Global Attributes Map
  globalAttributes?: {
    key: string;
    label: string;
    options: string[];
    categoryIds?: string[];
  }[];
}

export interface Theme {
  id: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
  isPreset?: boolean;
  celebrationMessage?: string;
}

export interface CartItem {
  id: string;
  productId: string;
  title: string;
  variantName?: string;
  price: number;
  imageUrl: string;
  quantity: number;
  shippingSize: ShippingSize; // Added to cart logic
}

export type DeliveryMethodType = 'courier' | 'paczkomat' | 'pickup';

export interface DeliveryDetails {
    method: DeliveryMethodType;
    cost: number;
    pointName?: string; // e.g. WAW20A
    pointAddress?: string;
}

export type OrderStatus = 'new' | 'paid' | 'processing' | 'shipped' | 'completed' | 'cancelled';

export interface Order {
    id: string; // Internal ID
    extOrderId: string; // Order ID visible to user (ORD_...)
    date: string;
    totalAmount: number;
    status: OrderStatus;
    customer: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        street?: string;
        city?: string;
        zipCode?: string;
    };
    items: CartItem[];
    delivery: DeliveryDetails;
    billing?: {
        nip: string;
        companyName: string;
        address: string;
    };
    paymentMethod?: string;
    documentType?: 'receipt' | 'receipt_nip' | 'invoice';
}

export interface AppState {
  pages: Page[];
  config: SiteConfig;
  isEditMode: boolean;
  themes: Theme[];
  currentThemeId: string;
  viewMode: 'admin' | 'public';
  cart: CartItem[]; // Shopping Cart
  isCartOpen: boolean;
  orders: Order[]; // Admin Order List
}

export interface SavedVersion {
  id: string;
  name: string;
  timestamp: number;
  state: AppState;
}
